import { getDatabase, ref, get, set, update, remove, push } from 'firebase/database';
import type { BuildingStructure } from '../types/protocols';
import { BlueprintLocation, PhotoModel } from '../types/protocols';
import { ParkingArea, ParkingAreaInput, ParkingSpot, ParkingSpotInput } from '../types/parking';
import { ParkingAreaKind, ParkingBlueprintSource, ParkingSpotType } from '../types/Enums';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { softDeleteDrawing, softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';
import { requireAdmin } from './adminGuard';

/** Tombstone entity types for parking files. Shared with iOS FileDeletionHelper. */
export const PARKING_SPOT_PHOTO_ENTITY_TYPE = 'parkingSpotPhoto';
export const PARKING_BLUEPRINT_ENTITY_TYPE = 'parkingBlueprint';

/**
 * Thrown when a level-mode spot location would be written under a level drawing
 * key that is no longer current. The hook refetches the building.
 */
export class StaleBlueprintError extends Error {
    constructor() {
        super('Våningens ritning har bytts sedan sidan laddades. Sidan har uppdaterats, placera platsen igen.');
        this.name = 'StaleBlueprintError';
    }
}

/** For level-mode placements: the level whose current drawing the location is keyed by. */
export interface LevelBlueprintGuard {
    levelKey: string;
}

const AREA_KINDS = Object.values(ParkingAreaKind) as string[];
const SPOT_TYPES = Object.values(ParkingSpotType) as string[];

/** RTDB rejects `undefined`; drop those keys before writing. */
const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) {
            result[key] = entry;
        }
    }
    return result as T;
};

/** For `update()`: `undefined` becomes `null` so cleared fields are removed. */
const nullForUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        result[key] = entry === undefined ? null : entry;
    }
    return result;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

const optionalNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Parking areas and spots at `buildings/{buildingKey}/parking/{areaKey}` and
 * `.../spots/{spotKey}`. Reads need a logged-in user; every mutation needs an
 * admin (plan D11 — matches buildingImagesApi). Area fields are written with
 * `update()` so `spots`/`blueprints` written by iOS are never clobbered (D3).
 */
export class ParkingApi {
    private db = getDatabase();

    getAreaPath(buildingKey: string, areaKey: string): string {
        return `buildings/${buildingKey}/parking/${areaKey}`;
    }

    getSpotPath(buildingKey: string, areaKey: string, spotKey: string): string {
        return `${this.getAreaPath(buildingKey, areaKey)}/spots/${spotKey}`;
    }

    /** DB path == Storage path for photos, like every other photo in the app. */
    getSpotPhotoPath(buildingKey: string, areaKey: string, spotKey: string, photoKey: string): string {
        return `${this.getSpotPath(buildingKey, areaKey, spotKey)}/photos/${photoKey}`;
    }

    getBlueprintPath(buildingKey: string, areaKey: string, blueprintKey: string): string {
        return `${this.getAreaPath(buildingKey, areaKey)}/blueprints/${blueprintKey}`;
    }

    // MARK: - Auth

    /** Admin or superadmin, like the rules and useIsAdmin (parking-tenants plan D6). */
    private async verifyAdmin(action: string): Promise<string> {
        return requireAdmin(this.db, action);
    }

    private stamp(uid: string): { updatedBy: string; updatedWhen: string } {
        return { updatedBy: uid, updatedWhen: formatDateForStorage(new Date()) };
    }

    // MARK: - Parsing

    parseParkingSpot(data: unknown, key: string): ParkingSpot | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        const type = typeof d.type === 'string' && SPOT_TYPES.includes(d.type)
            ? (d.type as ParkingSpotType)
            : ParkingSpotType.Car;
        return stripUndefined({
            key,
            number: typeof d.number === 'string' ? d.number : '',
            type,
            ceilingHeight: optionalNumber(d.ceilingHeight),
            chargerComponentKey: optionalString(d.chargerComponentKey),
            hasOutlet: typeof d.hasOutlet === 'boolean' ? d.hasOutlet : undefined,
            outletIdentifier: optionalString(d.outletIdentifier),
            blueprintLocations: d.blueprintLocations && typeof d.blueprintLocations === 'object'
                ? (d.blueprintLocations as { [imageKey: string]: BlueprintLocation })
                : undefined,
            photos: d.photos && typeof d.photos === 'object'
                ? (d.photos as { [photoKey: string]: PhotoModel })
                : undefined,
            comment: optionalString(d.comment),
            currentLeaseKey: optionalString(d.currentLeaseKey),
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    parseParkingArea(data: unknown, key: string): ParkingArea | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        const kind = typeof d.kind === 'string' && AREA_KINDS.includes(d.kind)
            ? (d.kind as ParkingAreaKind)
            : ParkingAreaKind.Garage;

        const spots: { [spotKey: string]: ParkingSpot } = {};
        if (d.spots && typeof d.spots === 'object') {
            for (const [spotKey, spotData] of Object.entries(d.spots as Record<string, unknown>)) {
                const spot = this.parseParkingSpot(spotData, spotKey);
                if (spot) {
                    spots[spotKey] = spot;
                } else {
                    console.warn(`Skipping invalid parking spot ${spotKey} in area ${key}`);
                }
            }
        }

        return stripUndefined({
            key,
            name: typeof d.name === 'string' ? d.name : '',
            kind,
            roomKey: optionalString(d.roomKey),
            blueprintSource: d.blueprintSource === ParkingBlueprintSource.Level
                ? ParkingBlueprintSource.Level
                : ParkingBlueprintSource.Own,
            defaultCeilingHeight: optionalNumber(d.defaultCeilingHeight),
            blueprints: d.blueprints && typeof d.blueprints === 'object'
                ? (d.blueprints as { [imageKey: string]: PhotoModel })
                : undefined,
            currentBlueprintKey: optionalString(d.currentBlueprintKey),
            spots,
            comment: optionalString(d.comment),
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    /** Parses the raw `parking` node of a building model; invalid areas are skipped. */
    parseAreas(data: unknown): { [areaKey: string]: ParkingArea } {
        const areas: { [areaKey: string]: ParkingArea } = {};
        if (!data || typeof data !== 'object') return areas;
        for (const [areaKey, areaData] of Object.entries(data as Record<string, unknown>)) {
            const area = this.parseParkingArea(areaData, areaKey);
            if (area) {
                areas[areaKey] = area;
            } else {
                console.warn(`Skipping invalid parking area ${areaKey}`);
            }
        }
        return areas;
    }

    // MARK: - Areas

    /**
     * Form input rules. Only a garage may have a room; `blueprintSource`
     * is not part of the input — level-mode rules are checked in updateArea
     * against the stored node.
     */
    private validateAreaInput(input: ParkingAreaInput): ParkingAreaInput {
        const name = input.name.trim();
        if (!name) {
            throw new Error('Namn krävs');
        }
        if (!AREA_KINDS.includes(input.kind)) {
            throw new Error('Ogiltig typ av parkeringsområde');
        }
        if (input.defaultCeilingHeight !== undefined && !(input.defaultCeilingHeight > 0)) {
            throw new Error('Takhöjd måste vara större än 0');
        }
        const roomKey = optionalString(input.roomKey?.trim());
        if (roomKey && input.kind !== ParkingAreaKind.Garage) {
            throw new Error('Endast garage kan kopplas till ett rum');
        }
        return {
            name,
            kind: input.kind,
            roomKey,
            defaultCeilingHeight: input.defaultCeilingHeight,
            comment: optionalString(input.comment?.trim()),
        };
    }

    private getRoomPath(buildingKey: string, roomKey: string): string {
        return `buildings/${buildingKey}/structure/rooms/${roomKey}`;
    }

    private getLevelPath(buildingKey: string, levelKey: string): string {
        return `buildings/${buildingKey}/structure/levels/${levelKey}`;
    }

    /** Reads the room fresh from the database; throws the missing-room copy when it is gone. */
    private async readRoomLevelKey(buildingKey: string, roomKey: string): Promise<string | undefined> {
        const snapshot = await get(ref(this.db, this.getRoomPath(buildingKey, roomKey)));
        if (!snapshot.exists()) {
            throw new Error('Rummet finns inte längre');
        }
        const room = snapshot.val() as Record<string, unknown> | null;
        return optionalString(typeof room?.levelKey === 'string' ? room.levelKey.trim() : undefined);
    }

    /** Level mode needs room → level; the level having no drawing is allowed. */
    private async assertRoomSupportsLevelMode(buildingKey: string, roomKey: string): Promise<void> {
        const levelKey = await this.readRoomLevelKey(buildingKey, roomKey);
        if (!levelKey) {
            throw new Error('Rummet saknar våning');
        }
        const levelSnapshot = await get(ref(this.db, this.getLevelPath(buildingKey, levelKey)));
        if (!levelSnapshot.exists()) {
            throw new Error('Våningen finns inte längre');
        }
    }

    private async readArea(buildingKey: string, areaKey: string): Promise<ParkingArea> {
        const snapshot = await get(ref(this.db, this.getAreaPath(buildingKey, areaKey)));
        const area = snapshot.exists() ? this.parseParkingArea(snapshot.val(), areaKey) : null;
        if (!area) {
            throw new Error('Parkeringsområdet hittades inte');
        }
        return area;
    }

    /** Every parking area of a building (with spots), keyed by area key; empty when there are none. */
    async getAreas(buildingKey: string): Promise<{ [areaKey: string]: ParkingArea }> {
        const snapshot = await get(ref(this.db, `buildings/${buildingKey}/parking`));
        return this.parseAreas(snapshot.exists() ? snapshot.val() : undefined);
    }

    /**
     * The rooms and levels of a building, which is all `resolveAreaBlueprint`
     * needs to find a level-mode area's drawing without loading the whole
     * building. Raw nodes, like the building model's structure.
     */
    async getBlueprintStructure(buildingKey: string): Promise<Pick<BuildingStructure, 'rooms' | 'levels'>> {
        const [rooms, levels] = await Promise.all([
            get(ref(this.db, `buildings/${buildingKey}/structure/rooms`)),
            get(ref(this.db, `buildings/${buildingKey}/structure/levels`)),
        ]);
        return {
            rooms: rooms.exists() ? (rooms.val() as BuildingStructure['rooms']) : undefined,
            levels: levels.exists() ? (levels.val() as BuildingStructure['levels']) : undefined,
        };
    }

    async createArea(buildingKey: string, input: ParkingAreaInput): Promise<ParkingArea> {
        const uid = await this.verifyAdmin('skapa parkeringsområden');
        const validated = this.validateAreaInput(input);
        if (validated.roomKey) {
            await this.readRoomLevelKey(buildingKey, validated.roomKey);
        }

        const newRef = push(ref(this.db, `buildings/${buildingKey}/parking`));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för parkeringsområdet');
        }

        const area: ParkingArea = stripUndefined({
            key,
            ...validated,
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });

        // Brand-new node: set() is safe, nothing nested exists yet.
        await set(ref(this.db, this.getAreaPath(buildingKey, key)), area);
        recordActivity('parkingArea.created', { buildingKey, targetKey: key, label: area.name });
        return area;
    }

    /**
     * Form save. Reads the stored area first so the input rules are checked against
     * the stored `blueprintSource`: in level mode a new room must still
     * lead to a level, and saving without a room or with kind ≠ garage adds
     * `blueprintSource: 'own'` to the same update. `blueprintSource` is never
     * written otherwise, so a form save cannot undo a drawing action.
     */
    async updateArea(buildingKey: string, areaKey: string, input: ParkingAreaInput): Promise<void> {
        const uid = await this.verifyAdmin('ändra parkeringsområden');
        const validated = this.validateAreaInput(input);
        const stored = await this.readArea(buildingKey, areaKey);
        const storedIsLevel = stored.blueprintSource === ParkingBlueprintSource.Level;

        const updates: Record<string, unknown> = {
            ...nullForUndefined({ ...validated }),
            ...this.stamp(uid),
        };

        // Only a changed room is checked; an unchanged one was checked when it was set.
        if (validated.roomKey && validated.roomKey !== stored.roomKey) {
            if (storedIsLevel && validated.kind === ParkingAreaKind.Garage) {
                await this.assertRoomSupportsLevelMode(buildingKey, validated.roomKey);
            } else {
                await this.readRoomLevelKey(buildingKey, validated.roomKey);
            }
        }
        if (storedIsLevel && (!validated.roomKey || validated.kind !== ParkingAreaKind.Garage)) {
            updates.blueprintSource = ParkingBlueprintSource.Own;
        }

        await update(ref(this.db, this.getAreaPath(buildingKey, areaKey)), updates);
        recordActivity('parkingArea.updated', { buildingKey, targetKey: areaKey, label: validated.name });
    }

    /**
     * Drawing action: "Använd våningens ritning" (`level`) or "Sluta
     * använda våningens ritning" (`own`). Never deletes or uploads anything;
     * own drawings and spot locations stay under their keys.
     */
    async setBlueprintSource(buildingKey: string, areaKey: string, source: ParkingBlueprintSource): Promise<void> {
        const uid = await this.verifyAdmin('ändra ritningar');
        if (source !== ParkingBlueprintSource.Level && source !== ParkingBlueprintSource.Own) {
            throw new Error('Ogiltig ritningskälla');
        }
        const area = await this.readArea(buildingKey, areaKey);
        if (source === ParkingBlueprintSource.Level) {
            if (area.kind !== ParkingAreaKind.Garage) {
                throw new Error('Endast garage kan använda våningens ritning');
            }
            if (!area.roomKey) {
                throw new Error('Koppla området till ett rum för att använda våningens ritning');
            }
            await this.assertRoomSupportsLevelMode(buildingKey, area.roomKey);
        }
        await update(ref(this.db, this.getAreaPath(buildingKey, areaKey)), {
            blueprintSource: source,
            ...this.stamp(uid),
        });
        recordActivity('drawing.updated', { buildingKey, targetKey: areaKey, label: area.name });
    }

    /** Guard for the drawing upload, which BlueprintUploadModal performs itself. */
    async assertAdminForDrawings(): Promise<void> {
        await this.verifyAdmin('ändra ritningar');
    }

    /**
     * Upload target for an own drawing. The pointer and `blueprintSource: 'own'`
     * are written in one multi-path update, so uploading while in level
     * mode also switches the area to its own drawing.
     */
    ownBlueprintUploadTarget(buildingKey: string, areaKey: string): {
        blueprintsPath: string;
        currentBlueprintKeyPath: string;
        extraPointerUpdates: Record<string, unknown>;
    } {
        const areaPath = this.getAreaPath(buildingKey, areaKey);
        return {
            blueprintsPath: `${areaPath}/blueprints`,
            currentBlueprintKeyPath: `${areaPath}/currentBlueprintKey`,
            extraPointerUpdates: { [`${areaPath}/blueprintSource`]: ParkingBlueprintSource.Own },
        };
    }

    /**
     * Freshness check before writing a spot location in level mode:
     * the building cache may hold an outdated level drawing key. Throws
     * StaleBlueprintError when the level's current drawing is no longer `imageKey`.
     */
    async assertLevelBlueprintCurrent(buildingKey: string, levelKey: string, imageKey: string): Promise<void> {
        const snapshot = await get(ref(this.db, `${this.getLevelPath(buildingKey, levelKey)}/currentBlueprintKey`));
        const current = snapshot.exists() ? snapshot.val() : undefined;
        if (current !== imageKey) {
            throw new StaleBlueprintError();
        }
    }

    /** Stamps the area after a drawing upload (the modal writes the drawing nodes). */
    async stampAreaUpdated(buildingKey: string, areaKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ändra ritningar');
        await update(ref(this.db, this.getAreaPath(buildingKey, areaKey)), this.stamp(uid));
    }

    /**
     * Tombstones every drawing and spot photo, then removes the area node.
     * Per file and not atomic — accepted in plan review F6.
     */
    async deleteArea(buildingKey: string, areaKey: string): Promise<void> {
        await this.verifyAdmin('ta bort parkeringsområden');
        const areaPath = this.getAreaPath(buildingKey, areaKey);
        const snapshot = await get(ref(this.db, areaPath));
        if (!snapshot.exists()) {
            throw new Error('Parkeringsområdet hittades inte');
        }
        const area = this.parseParkingArea(snapshot.val(), areaKey);
        if (area) {
            // A rented spot holds a lease (plan D14); ended leases keep their label and never block.
            if (Object.values(area.spots || {}).some(spot => spot.currentLeaseKey)) {
                throw new Error('Platsen har ett aktivt hyresavtal');
            }
            for (const blueprintKey of Object.keys(area.blueprints || {})) {
                await softDeleteDrawing({
                    originalPath: this.getBlueprintPath(buildingKey, areaKey, blueprintKey),
                    entityType: PARKING_BLUEPRINT_ENTITY_TYPE,
                });
            }
            for (const spot of Object.values(area.spots || {})) {
                for (const photoKey of Object.keys(spot.photos || {})) {
                    await softDeleteFile({
                        originalPath: this.getSpotPhotoPath(buildingKey, areaKey, spot.key, photoKey),
                        entityType: PARKING_SPOT_PHOTO_ENTITY_TYPE,
                    });
                }
            }
        }
        await remove(ref(this.db, areaPath));
        recordActivity('parkingArea.deleted', { buildingKey, targetKey: areaKey, label: area?.name });
    }

    /**
     * Own mode only: tombstones the current own drawing and clears the pointer;
     * older drawings stay. In level mode the drawing belongs to the level and is
     * never tombstoned from the area — use setBlueprintSource('own').
     */
    async deleteCurrentBlueprint(buildingKey: string, areaKey: string, blueprintKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ta bort ritningar');
        const area = await this.readArea(buildingKey, areaKey);
        if (area.blueprintSource === ParkingBlueprintSource.Level) {
            throw new Error('Området använder våningens ritning, som inte kan tas bort här');
        }
        if (!area.blueprints?.[blueprintKey]) {
            throw new Error('Ritningen hittades inte');
        }
        await softDeleteDrawing({
            originalPath: this.getBlueprintPath(buildingKey, areaKey, blueprintKey),
            entityType: PARKING_BLUEPRINT_ENTITY_TYPE,
            extraUpdates: {
                [`${this.getAreaPath(buildingKey, areaKey)}/currentBlueprintKey`]: null,
                [`${this.getAreaPath(buildingKey, areaKey)}/updatedBy`]: uid,
                [`${this.getAreaPath(buildingKey, areaKey)}/updatedWhen`]: formatDateForStorage(new Date()),
            },
        });
    }

    // MARK: - Spots

    private validateSpotInput(input: ParkingSpotInput): ParkingSpotInput {
        const number = input.number.trim();
        if (!number) {
            throw new Error('Platsnummer krävs');
        }
        if (!SPOT_TYPES.includes(input.type)) {
            throw new Error('Ogiltig typ av plats');
        }
        if (input.ceilingHeight !== undefined && !(input.ceilingHeight > 0)) {
            throw new Error('Fri höjd måste vara större än 0');
        }
        const hasOutlet = input.hasOutlet === true ? true : undefined;
        return {
            number,
            type: input.type,
            ceilingHeight: input.ceilingHeight,
            chargerComponentKey: optionalString(input.chargerComponentKey),
            hasOutlet,
            outletIdentifier: hasOutlet ? optionalString(input.outletIdentifier?.trim()) : undefined,
            comment: optionalString(input.comment?.trim()),
        };
    }

    async createSpot(
        buildingKey: string,
        areaKey: string,
        input: ParkingSpotInput,
        blueprintLocation?: BlueprintLocation,
        levelGuard?: LevelBlueprintGuard
    ): Promise<ParkingSpot> {
        const uid = await this.verifyAdmin('skapa parkeringsplatser');
        const validated = this.validateSpotInput(input);
        if (blueprintLocation && levelGuard) {
            await this.assertLevelBlueprintCurrent(buildingKey, levelGuard.levelKey, blueprintLocation.key);
        }

        const newRef = push(ref(this.db, `${this.getAreaPath(buildingKey, areaKey)}/spots`));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för parkeringsplatsen');
        }

        const spot: ParkingSpot = stripUndefined({
            key,
            ...validated,
            blueprintLocations: blueprintLocation
                ? { [blueprintLocation.key]: blueprintLocation }
                : undefined,
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });

        await set(ref(this.db, this.getSpotPath(buildingKey, areaKey, key)), spot);
        recordActivity('parkingSpot.created', { buildingKey, targetKey: key, label: spot.number });
        return spot;
    }

    async updateSpot(buildingKey: string, areaKey: string, spotKey: string, input: ParkingSpotInput): Promise<void> {
        const uid = await this.verifyAdmin('ändra parkeringsplatser');
        const validated = this.validateSpotInput(input);
        await update(ref(this.db, this.getSpotPath(buildingKey, areaKey, spotKey)), {
            ...nullForUndefined({ ...validated }),
            ...this.stamp(uid),
        });
        recordActivity('parkingSpot.updated', { buildingKey, targetKey: spotKey, label: validated.number });
    }

    /**
     * Sets or clears (location = null) the spot's marker for one drawing. Pass
     * `levelGuard` when placing in level mode: the level's current drawing key is
     * re-read first and the write is refused if the cached key is stale.
     */
    async setSpotLocation(
        buildingKey: string,
        areaKey: string,
        spotKey: string,
        imageKey: string,
        location: BlueprintLocation | null,
        levelGuard?: LevelBlueprintGuard
    ): Promise<void> {
        const uid = await this.verifyAdmin('placera parkeringsplatser');
        if (location && levelGuard) {
            await this.assertLevelBlueprintCurrent(buildingKey, levelGuard.levelKey, imageKey);
        }
        await update(ref(this.db, this.getSpotPath(buildingKey, areaKey, spotKey)), {
            [`blueprintLocations/${imageKey}`]: location ? { ...location, key: imageKey } : null,
            ...this.stamp(uid),
        });
        recordActivity('parkingSpot.updated', { buildingKey, targetKey: spotKey });
    }

    async deleteSpot(buildingKey: string, areaKey: string, spotKey: string): Promise<void> {
        await this.verifyAdmin('ta bort parkeringsplatser');
        const spotPath = this.getSpotPath(buildingKey, areaKey, spotKey);
        const snapshot = await get(ref(this.db, spotPath));
        if (!snapshot.exists()) {
            throw new Error('Parkeringsplatsen hittades inte');
        }
        const spot = this.parseParkingSpot(snapshot.val(), spotKey);
        if (spot?.currentLeaseKey) {
            throw new Error('Platsen har ett aktivt hyresavtal');
        }
        for (const photoKey of Object.keys(spot?.photos || {})) {
            await softDeleteFile({
                originalPath: this.getSpotPhotoPath(buildingKey, areaKey, spotKey, photoKey),
                entityType: PARKING_SPOT_PHOTO_ENTITY_TYPE,
            });
        }
        await remove(ref(this.db, spotPath));
        recordActivity('parkingSpot.deleted', { buildingKey, targetKey: spotKey, label: spot?.number });
    }

    // MARK: - Photos

    /** Guard + path config for usePhotoUpload; the hook does the actual upload. */
    async prepareSpotPhotoUpload(
        buildingKey: string,
        areaKey: string,
        spotKey: string
    ): Promise<{ storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }> {
        await this.verifyAdmin('ladda upp foton');
        const path = (photoKey: string) => this.getSpotPhotoPath(buildingKey, areaKey, spotKey, photoKey);
        return { storagePath: path, dbPath: path };
    }

    async stampSpotUpdated(buildingKey: string, areaKey: string, spotKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ändra parkeringsplatser');
        await update(ref(this.db, this.getSpotPath(buildingKey, areaKey, spotKey)), this.stamp(uid));
    }

    async deleteSpotPhoto(buildingKey: string, areaKey: string, spotKey: string, photoKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ta bort foton');
        const spotPath = this.getSpotPath(buildingKey, areaKey, spotKey);
        await softDeleteFile({
            originalPath: this.getSpotPhotoPath(buildingKey, areaKey, spotKey, photoKey),
            entityType: PARKING_SPOT_PHOTO_ENTITY_TYPE,
            extraUpdates: {
                [`${spotPath}/updatedBy`]: uid,
                [`${spotPath}/updatedWhen`]: formatDateForStorage(new Date()),
            },
        });
    }
}

// Export singleton instance
export const parkingApi = new ParkingApi();
